import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import * as auth from "../_redux/authRedux";
import { login } from "../_redux/authCrud";
import Cookies from 'js-cookie';

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const initialValues = {
  email: "",
  password: "",
};

function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const passwordToggleLabel = intl.formatMessage({
    id: showPassword ? "AUTH.INPUT.HIDE_PASSWORD" : "AUTH.INPUT.SHOW_PASSWORD",
    defaultMessage: showPassword ? "Ocultar contraseña" : "Mostrar contraseña",
  });
  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(
        intl.formatMessage({
          id: "AUTH.VALIDATION.INVALID_EMAIL",
        })
      )
      .min(
        3,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MIN_SYMBOLS",
          },
          { min: 3 }
        )
      )
      .max(
        128,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MAX_SYMBOLS",
          },
          { max: 128 }
        )
      )
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.REQUIRED_FIELD",
        })
      ),
    password: Yup.string()
      .min(
        3,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MIN_SYMBOLS",
          },
          { min: 3 }
        )
      )
      .max(
        50,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MAX_SYMBOLS",
          },
          { max: 50 }
        )
      )
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.REQUIRED_FIELD",
        })
      ),
  });

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      enableLoading();
      setTimeout(() => {
        login(values.email, values.password)
          .then((res) => {
            disableLoading();
            Cookies.set("session_token", res.data.data.token, {
              path: "/",
              sameSite: "strict",
              secure: window.location.protocol === "https:",
            });
            window.location.reload();
          })
          .catch(() => {
            setStatus(
              intl.formatMessage({
                id: "AUTH.VALIDATION.INVALID_LOGIN",
              })
            );
          })
          .finally(() => {
            disableLoading();
            setSubmitting(false);
          });
      }, 1000);
    },
  });

  return (
    <div className="login-form login-signin" id="kt_login_signin_form">
      {/* begin::Head */}
      <div className="logo-login mb-10">
      </div>
      <div className="text-center mb-lg-10">
        <h3 className="font-size-h3">
          {intl.formatMessage({ id: "AUTH.LOGIN.TITLE" })}
        </h3>
      </div>
      {/* end::Head */}

      {/*begin::Form*/}
      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {formik.status ? (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        ) : (
          <>
          </>
        )}

        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder={intl.formatMessage({ id: "AUTH.INPUT.EMAIL" })}
            type="email"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              "email"
            )}`}
            name="email"
            {...formik.getFieldProps("email")}
          />
          {formik.touched.email && formik.errors.email ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.email}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group fv-plugins-icon-container">
          <div className="admin-password-field">
            <input
              placeholder={intl.formatMessage({ id: "AUTH.INPUT.PASSWORD" })}
              type={showPassword ? "text" : "password"}
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
                "password"
              )}`}
              name="password"
              autoComplete="current-password"
              {...formik.getFieldProps("password")}
            />
            <button
              type="button"
              className="admin-password-toggle"
              aria-label={passwordToggleLabel}
              aria-pressed={showPassword}
              title={passwordToggleLabel}
              onClick={() => setShowPassword((visible) => !visible)}
            >
              {showPassword
                ? <FaEyeSlash aria-hidden="true" focusable="false" />
                : <FaEye aria-hidden="true" focusable="false" />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.password}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
          <button
            id="kt_login_signin_submit"
            type="submit"
            disabled={formik.isSubmitting}
            style={{width: '100%'}}
            className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
          >
            <span>{intl.formatMessage({ id: "AUTH.LOGIN.BUTTON" })}</span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>
        </div>
      </form>
      {/*end::Form*/}
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
